import { useState, useRef, useEffect, useCallback, useId } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import type { SelectProps } from './types'
import styles from './Select.module.css'

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Selecione uma opção',
  label,
  disabled = false,
  className,
  id,
  icon,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Encontra a opção selecionada
  const selectedOption = options.find((opt) => opt.value === value)
  const displayValue = selectedOption?.label || placeholder
  const hasSubtitle = Boolean(selectedOption?.subtitle)

  // Fecha ao clicar fora (ignora cliques na lista, que pode estar em portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (listRef.current?.contains(target)) return
      setIsOpen(false)
      setHighlightedIndex(-1)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  // Scroll para opção selecionada quando abre (setState em microtask para evitar set-state-in-effect)
  useEffect(() => {
    if (isOpen && listRef.current && value) {
      const selectedIndex = options.findIndex((opt) => opt.value === value)
      if (selectedIndex >= 0) {
        const optionElement = listRef.current.children[selectedIndex] as HTMLElement
        if (optionElement) {
          optionElement.scrollIntoView({ block: 'nearest' })
          queueMicrotask(() => setHighlightedIndex(selectedIndex))
        }
      }
    }
  }, [isOpen, value, options])

  // Handler para toggle do dropdown
  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev)
      if (!isOpen) {
        setHighlightedIndex(-1)
      }
    }
  }, [disabled, isOpen])

  // Handler para seleção de opção
  const handleSelect = useCallback(
    (optionValue: string) => {
      if (!disabled) {
        onChange?.(optionValue)
        setIsOpen(false)
        setHighlightedIndex(-1)
        triggerRef.current?.focus()
      }
    },
    [disabled, onChange]
  )

  // Navegação por teclado
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (isOpen && highlightedIndex >= 0) {
            const option = options[highlightedIndex]
            if (option && !option.disabled) {
              handleSelect(option.value)
            }
          } else {
            handleToggle()
          }
          break

        case 'ArrowDown':
          e.preventDefault()
          if (!isOpen) {
            setIsOpen(true)
          } else {
            const nextIndex = options.findIndex(
              (opt, idx) => idx > highlightedIndex && !opt.disabled
            )
            if (nextIndex >= 0) {
              setHighlightedIndex(nextIndex)
            } else {
              // Vai para a primeira opção habilitada
              const firstEnabled = options.findIndex((opt) => !opt.disabled)
              if (firstEnabled >= 0) {
                setHighlightedIndex(firstEnabled)
              }
            }
          }
          break

        case 'ArrowUp':
          e.preventDefault()
          if (isOpen) {
            const prevIndex = options
              .map((opt, idx) => ({ opt, idx }))
              .reverse()
              .find(({ opt, idx }) => idx < highlightedIndex && !opt.disabled)
            if (prevIndex) {
              setHighlightedIndex(prevIndex.idx)
            } else {
              // Vai para a última opção habilitada
              const lastEnabled = options
                .map((opt, idx) => ({ opt, idx }))
                .reverse()
                .find(({ opt }) => !opt.disabled)
              if (lastEnabled) {
                setHighlightedIndex(lastEnabled.idx)
              }
            }
          }
          break

        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setHighlightedIndex(-1)
          triggerRef.current?.focus()
          break

        case 'Tab':
          setIsOpen(false)
          setHighlightedIndex(-1)
          break

        default:
          break
      }
    },
    [disabled, isOpen, highlightedIndex, options, handleSelect, handleToggle]
  )

  // Scroll para opção destacada
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const optionElement = listRef.current.children[highlightedIndex] as HTMLElement
      if (optionElement) {
        optionElement.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [highlightedIndex, isOpen])

  const generatedId = useId()
  const selectId = id || generatedId
  const listId = `${selectId}-list`
  const [listPosition, setListPosition] = useState({ top: 0, left: 0, width: 0 })

  // Calcula posição da lista quando abre
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setListPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }
  }, [isOpen])

  const listElement = isOpen ? (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      className={styles.list}
      aria-label={label || 'Opções'}
      style={{
        top: `${listPosition.top}px`,
        left: `${listPosition.left}px`,
        width: `${listPosition.width}px`,
      }}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value
        const isHighlighted = index === highlightedIndex
        const isDisabled = option.disabled || disabled

        return (
          <li
            key={option.value}
            role="option"
            aria-selected={isSelected}
            aria-disabled={isDisabled}
            className={[
              styles.option,
              isSelected && styles.optionSelected,
              isHighlighted && styles.optionHighlighted,
              isDisabled && styles.optionDisabled,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => !isDisabled && handleSelect(option.value)}
            onMouseEnter={() => !isDisabled && setHighlightedIndex(index)}
          >
            <span className={option.subtitle ? styles.optionTextWrap : undefined}>
              <span className={styles.optionLabel}>{option.label}</span>
              {option.subtitle && (
                <span className={styles.optionSubtitle}>{option.subtitle}</span>
              )}
            </span>
            {isSelected && (
              <Icon icon="mdi:check" className={styles.checkIcon} />
            )}
          </li>
        )
      })}
    </ul>
  ) : null

  return (
    <div
      ref={containerRef}
      className={[styles.selectContainer, className].filter(Boolean).join(' ')}
    >
      {label && (
        <label htmlFor={selectId} className={styles.label}>
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        id={selectId}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={[
          styles.trigger,
          isOpen && styles.triggerOpen,
          disabled && styles.triggerDisabled,
          !selectedOption && styles.triggerPlaceholder,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.triggerContent}>
          {icon && <Icon icon={icon} className={styles.triggerIcon} />}
          <span className={hasSubtitle ? styles.triggerTextWrap : styles.triggerText}>
            {hasSubtitle && selectedOption ? (
              <>
                <span className={styles.triggerLabel}>{selectedOption.label}</span>
                <span className={styles.triggerSubtitle}>{selectedOption.subtitle}</span>
              </>
            ) : (
              displayValue
            )}
          </span>
        </span>
        <Icon
          icon="mdi:chevron-down"
          className={[styles.chevron, isOpen && styles.chevronOpen]
            .filter(Boolean)
            .join(' ')}
        />
      </button>

      {typeof document !== 'undefined' && createPortal(listElement, document.body)}
    </div>
  )
}
